/**
 * Achievements Page
 * Displays all achievements with unlock status
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Trophy, Lock, Star, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const RARITY_STYLES = {
  common: { 
    bg: 'bg-slate-500/10', 
    border: 'border-slate-500/30', 
    text: 'text-slate-400',
    glow: ''
  },
  uncommon: { 
    bg: 'bg-emerald-500/10', 
    border: 'border-emerald-500/30', 
    text: 'text-emerald-400',
    glow: 'shadow-emerald-500/10'
  },
  rare: { 
    bg: 'bg-cyan-500/10', 
    border: 'border-cyan-500/30', 
    text: 'text-cyan-400',
    glow: 'shadow-cyan-500/20'
  },
  legendary: { 
    bg: 'bg-amber-500/10', 
    border: 'border-amber-500/30', 
    text: 'text-amber-400',
    glow: 'shadow-amber-500/20'
  }
};

const CATEGORY_LABELS = {
  chess: { name: 'Chess', emoji: '♟️' },
  checkers: { name: 'Checkers', emoji: '⚫' },
  baseball: { name: 'Baseball', emoji: '⚾' },
  bridge: { name: 'Bridge', emoji: '🃏' },
  general: { name: 'General', emoji: '🎮' }
};

const AchievementCard = ({ achievement, unlocked, unlocked_at }) => {
  const styles = RARITY_STYLES[achievement.rarity] || RARITY_STYLES.common;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`
        relative p-4 rounded-xl border ${styles.border} ${styles.bg}
        ${unlocked ? `shadow-lg ${styles.glow}` : 'opacity-60'}
        transition-all duration-200
      `}
    >
      {/* Lock overlay for locked achievements */}
      {!unlocked && (
        <div className="absolute inset-0 flex items-center justify-center bg-slate-900/50 rounded-xl">
          <Lock className="w-6 h-6 text-slate-600" />
        </div>
      )}
      
      <div className="flex items-start gap-3">
        <div className={`text-3xl ${!unlocked && 'grayscale opacity-50'}`}>
          {achievement.icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className={`font-semibold text-sm ${unlocked ? 'text-white' : 'text-slate-500'}`}>
              {achievement.name}
            </h3>
            <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${styles.bg} ${styles.text}`}>
              {achievement.rarity}
            </span>
          </div>
          <p className="text-xs text-slate-500 mb-2">{achievement.description}</p>
          <div className="flex items-center gap-3">
            <span className={`text-xs font-mono ${styles.text}`}>
              +{achievement.points} pts
            </span>
            {unlocked && unlocked_at && (
              <span className="text-xs text-slate-600">
                {new Date(unlocked_at).toLocaleDateString()}
              </span>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const AchievementsPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, token } = useAuth();
  const [achievements, setAchievements] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/auth?mode=login');
      return;
    }
    
    fetchAchievements();
    fetchSummary();
  }, [isAuthenticated, token]);

  const fetchAchievements = async () => {
    try {
      const res = await fetch(`${API_URL}/api/achievements`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAchievements(data);
      }
    } catch (err) {
      console.error('Failed to fetch achievements:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const res = await fetch(`${API_URL}/api/achievements/summary`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSummary(data);
      }
    } catch (err) {
      console.error('Failed to fetch summary:', err);
    }
  };

  const filteredAchievements = selectedCategory === 'all' 
    ? achievements 
    : achievements.filter(a => a.achievement.category === selectedCategory);

  const categories = ['all', 'general', 'chess', 'checkers', 'baseball', 'bridge'];

  if (!isAuthenticated) return null;

  return (
    <div className="min-h-screen bg-obsidian">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-obsidian/90 backdrop-blur-md border-b border-slate-800">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/dashboard')}
            className="text-slate-400 hover:text-white"
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="flex items-center gap-3">
            <Trophy className="w-6 h-6 text-amber-400" />
            <h1 className="text-xl font-bold text-white">Achievements</h1>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-obsidian-card border border-slate-800 rounded-xl p-4 text-center">
              <div className="text-3xl font-bold text-white">{summary.unlocked_count}</div>
              <div className="text-xs text-slate-500">Unlocked</div>
            </div>
            <div className="bg-obsidian-card border border-slate-800 rounded-xl p-4 text-center">
              <div className="text-3xl font-bold text-slate-400">{summary.total_count}</div>
              <div className="text-xs text-slate-500">Total</div>
            </div>
            <div className="bg-obsidian-card border border-amber-500/30 rounded-xl p-4 text-center">
              <div className="text-3xl font-bold text-amber-400">{summary.total_points}</div>
              <div className="text-xs text-slate-500">Points</div>
            </div>
            <div className="bg-obsidian-card border border-slate-800 rounded-xl p-4 text-center">
              <div className="text-3xl font-bold text-purple-400">
                {Math.round((summary.unlocked_count / summary.total_count) * 100)}%
              </div>
              <div className="text-xs text-slate-500">Complete</div>
            </div>
          </div>
        )}

        {/* Rarity breakdown */}
        {summary && (
          <div className="flex gap-3 mb-6 flex-wrap">
            {Object.entries(summary.rarity_counts || {}).map(([rarity, count]) => {
              const styles = RARITY_STYLES[rarity];
              return (
                <div 
                  key={rarity}
                  className={`px-3 py-1.5 rounded-lg ${styles.bg} border ${styles.border}`}
                >
                  <span className={`text-xs font-semibold ${styles.text}`}>
                    {count} {rarity}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {/* Category Filter */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`
                px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all
                ${selectedCategory === cat 
                  ? 'bg-purple-600 text-white' 
                  : 'bg-slate-800 text-slate-400 hover:bg-slate-700'}
              `}
            >
              {cat === 'all' ? '🎯 All' : `${CATEGORY_LABELS[cat]?.emoji || ''} ${CATEGORY_LABELS[cat]?.name || cat}`}
            </button>
          ))}
        </div>

        {/* Achievements Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredAchievements.map((item, idx) => (
              <AchievementCard 
                key={item.achievement.id}
                achievement={item.achievement}
                unlocked={item.unlocked}
                unlocked_at={item.unlocked_at}
              />
            ))}
          </div>
        )}

        {filteredAchievements.length === 0 && !loading && (
          <div className="text-center py-12">
            <Sparkles className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-500">No achievements in this category yet</p>
          </div>
        )}
      </main>
    </div>
  );
};

export default AchievementsPage;
